import { END_POINTS } from "../constants/urls";
import emptySplitApi from "../utils/rtk";

type CommonResponse = {
  status: number;
  data?: any;
  message: string;
};

export const bookingsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getBookings: builder.query<
      CommonResponse,
      {
        search: string;
        page: number;
        categoryId?: string | null;
        status?: string;
        body?: any;
        sortOrder?: string;
      }
    >({
      query: ({ search, page, categoryId, status, body, sortOrder }) => {
        const params = new URLSearchParams({
          search,
          pageNumber: String(page),
        });

        if (categoryId) {
          params.append("categoryId", categoryId);
        }

        if (status) {
          params.append("bookingStatus", status);
        }

        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }

        return {
          url: `${END_POINTS.getBookings}?${params.toString()}`,
          method: "POST",
          body,
        };
      },
    }),

    allEmployees: builder.query<CommonResponse, {}>({
      query: () => ({
        url: `${END_POINTS.allEmployees}`,
        method: "GET",
      }),
    }),

    assignEmployee: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.assignEmployee,
        method: "POST",
        body,
      }),
    }),

    bookingDetails: builder.query<CommonResponse, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `${END_POINTS.bookingDetail}?BookingId=${id}`,
        method: "GET",
      }),
    }),

    getNotes: builder.query<
      CommonResponse,
      { id: string | undefined; page: number; search: string }
    >({
      query: ({ id, page, search }) => ({
        url: `${END_POINTS.getNotes}?BookingId=${id}&pageNumber=${page}&search=${search}`,
        method: "GET",
      }),
    }),

    addNotes: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.addNotes,
        method: "POST",
        body,
      }),
    }),

    getCancelledBookings: builder.query<
      CommonResponse,
      {
        search: string;
        page: number;
        categoryId?: string | null;
        status?: string;
        sortOrder?: string;
        body?: any;
      }
    >({
      query: ({ search, page, categoryId, status, sortOrder, body }) => {
        const params = new URLSearchParams({
          search,
          pageNumber: String(page),
        });

        if (categoryId) {
          params.append("categoryId", categoryId);
        }

        if (status) {
          params.append("status", status);
        }

        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }

        return {
          url: `${END_POINTS.cancelledBookings}?${params.toString()}`,
          method: "POST",
          body,
        };
      },
    }),

    addExceptionalGuest: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.exceptionalGuest,
        method: "POST",
        body,
      }),
    }),

    refundMembers: builder.query<CommonResponse, { bookingId: string }>({
      query: ({ bookingId }) => ({
        url: `${END_POINTS.refundMembers}?bookingId=${bookingId}`,
        method: "GET",
      }),
    }),

    cancellationRefund: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.cancellationRefund,
        method: "POST",
        body,
      }),
    }),

    editBooking: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.editBooking,
        method: "PATCH",
        body,
      }),
    }),

    editHistory: builder.query<any, { bookingId: string }>({
      query: ({ bookingId }) => ({
        url: `${END_POINTS.editHistory}/${bookingId}/log`,
        method: "GET",
      }),
    }),

    refundGuest: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.refundGuest,
        method: "POST",
        body,
      }),
    }),

    clearBookingBadge: builder.mutation<CommonResponse, any>({
      query: () => ({
        url: END_POINTS.clearBookingCount,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLazyGetBookingsQuery,
  useLazyAllEmployeesQuery,
  useAssignEmployeeMutation,
  useLazyBookingDetailsQuery,
  useLazyGetNotesQuery,
  useAddNotesMutation,
  useLazyGetCancelledBookingsQuery,
  useAddExceptionalGuestMutation,
  useLazyRefundMembersQuery,
  useCancellationRefundMutation,
  useEditBookingMutation,
  useLazyEditHistoryQuery,
  useRefundGuestMutation,
  useClearBookingBadgeMutation,
} = bookingsApi;
